import { useNavigate, useSearchParams } from 'react-router';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { InputField } from '@/kit/FormField';
import { PageHeader } from '@/kit/PageHeader';
import { Select } from '@/kit/Select';
import { TabGroup } from '@/kit/Tabs';
import { notifyError } from '@/core/notify/notify';
import { managementReportingService } from '@/core/services/managementReporting';
import type { ExportFormatCode } from '@/core/models/practice.models';
import {
  managementMoney,
  managementPercent,
  type DimensionRow,
  type ManagementExportQuery,
  type ManagementPeriodRow,
  type MultiYearReportResponse,
  type PeriodicBalanceRow,
  type PeriodicTrialBalanceResponse,
  type VarianceReportResponse,
  type VarianceRow,
} from '@/core/models/management-reporting.models';
import {
  dimensionValuesOf,
  ExportMenu,
  guardClient,
  MatButton,
  snapshotSummary,
  useLiveState,
  useOnInit,
  useSelectedClient,
} from './shared';
import './PeriodicTrialbalance.scss';

interface State {
  periods: ManagementPeriodRow[];
  dimensions: DimensionRow[];
  dimensionValues: { code: string; name: string }[];
  selectedPeriodId: string | null;
  selectedPriorPeriodId: string | null;
  selectedDimensionCode: string | null;
  selectedDimensionValue: string | null;
  /** A plain text field: a string once it has been edited (Angular kept whatever ngModel held). */
  years: number | string;
  selectedTab: number;
  periodic: PeriodicTrialBalanceResponse | null;
  variance: VarianceReportResponse | null;
  multiYear: MultiYearReportResponse | null;
  periodicColumns: string[];
  varianceColumns: string[];
}

const PERIODIC_COLUMNS = ['code', 'name', 'type', 'debit', 'credit', 'amount'];
const PERIODIC_COLUMNS_WITH_DIMENSION = ['code', 'name', 'type', 'dimension', 'debit', 'credit', 'amount'];
const VARIANCE_COLUMNS = ['code', 'name', 'current', 'prior', 'budget', 'budgetvariance', 'budgetpercent'];
const VARIANCE_COLUMNS_WITH_DIMENSION = [
  'code',
  'name',
  'dimension',
  'current',
  'prior',
  'budget',
  'budgetvariance',
  'budgetpercent',
];

const money = managementMoney;
const percent = managementPercent;
const NUMERIC = 'w-150 text-align-right';

/** Periodic trial balance, variance and multi-year management reporting (Angular TaxreturnPeriodicTrialbalanceComponent). */
export function TaxreturnPeriodicTrialbalance() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const client = useSelectedClient();
  const [s, set, live] = useLiveState<State>({
    periods: [],
    dimensions: [],
    dimensionValues: [],
    selectedPeriodId: null,
    selectedPriorPeriodId: null,
    selectedDimensionCode: null,
    selectedDimensionValue: null,
    years: 3,
    selectedTab: 0,
    periodic: null,
    variance: null,
    multiYear: null,
    periodicColumns: PERIODIC_COLUMNS,
    varianceColumns: VARIANCE_COLUMNS,
  });

  useOnInit(() => {
    if (!guardClient(client, navigate)) {
      return;
    }

    managementReportingService.getDimensions(client.id).then(
      (rows) => set({ dimensions: rows }),
      (error) => notifyError(error),
    );

    const requested = params.get('id');
    managementReportingService.getManagementPeriods(client.id).then(
      (rows) => {
        set({
          periods: rows,
          selectedPeriodId: requested && rows.some((r) => r.id === requested) ? requested : (rows[0]?.id ?? null),
        });
        if (live.current.selectedPeriodId) {
          generate();
        }
      },
      (error) => notifyError(error),
    );
  });

  // The strictly earlier periods (ISO dates compare as strings).
  const selectedPeriod = s.periods.find((p) => p.id === s.selectedPeriodId);
  const priorPeriods = selectedPeriod
    ? s.periods.filter((p) => p.id !== selectedPeriod.id && p.endDate < selectedPeriod.startDate)
    : [];
  /** #184: the selected management period has no snapshot, so every report here is empty. */
  const selectedPeriodHasNoSnapshot = !!selectedPeriod && !selectedPeriod.snapshotAtUtc;

  /** #183: the management period whose figures the open tab shows (not the picker, which may not be generated yet). */
  const shownPeriod: ManagementPeriodRow | undefined =
    s.selectedTab === 1
      ? s.periods.find((p) => p.id === s.variance?.managementPeriodId)
      : s.selectedTab === 2
        ? s.periods.find((p) => p.id === s.multiYear?.managementPeriodId)
        : (s.periodic?.period ?? undefined);

  function onDimensionChange(): void {
    set({
      dimensionValues: dimensionValuesOf(live.current.dimensions, live.current.selectedDimensionCode),
      selectedDimensionValue: null,
    });
  }

  function onTabChange(index: number): void {
    set({ selectedTab: index });
    generate();
  }

  const openManagementPeriods = () => navigate('/home/taxreturns/reports-managementperiods');

  function generate(): void {
    const st = live.current;
    if (!st.selectedPeriodId) {
      notifyError('Select a management period first.');
      return;
    }
    const id = st.selectedPeriodId;

    if (st.selectedTab === 1) {
      managementReportingService
        .getVarianceReport(id, st.selectedPriorPeriodId, st.selectedDimensionCode, st.selectedDimensionValue)
        .then(
          (report) =>
            set({
              variance: report,
              varianceColumns: live.current.selectedDimensionCode ? VARIANCE_COLUMNS_WITH_DIMENSION : VARIANCE_COLUMNS,
            }),
          (error) => notifyError(error),
        );
      return;
    }

    if (st.selectedTab === 2) {
      managementReportingService
        .getMultiYearReport(id, st.years as number, st.selectedDimensionCode, st.selectedDimensionValue)
        .then(
          (report) => set({ multiYear: report }),
          (error) => notifyError(error),
        );
      return;
    }

    managementReportingService.getPeriodicTrialBalance(id, st.selectedDimensionCode, st.selectedDimensionValue).then(
      (report) =>
        set({
          periodic: report,
          periodicColumns: live.current.selectedDimensionCode ? PERIODIC_COLUMNS_WITH_DIMENSION : PERIODIC_COLUMNS,
        }),
      (error) => notifyError(error),
    );
  }

  function exportReport(format: ExportFormatCode): void {
    const st = live.current;
    if (!st.selectedPeriodId) {
      notifyError('Generate the report before exporting it.');
      return;
    }
    const query: ManagementExportQuery = {
      format,
      dimensionCode: st.selectedDimensionCode,
      dimensionValue: st.selectedDimensionValue,
      priorManagementPeriodId: st.selectedPriorPeriodId,
      years: st.years as number,
    };
    const id = st.selectedPeriodId;

    const request =
      st.selectedTab === 1
        ? managementReportingService.exportVarianceReport(id, query)
        : st.selectedTab === 2
          ? managementReportingService.exportMultiYearReport(id, query)
          : managementReportingService.exportPeriodicTrialBalance(id, query);

    request.then(undefined, (error) => notifyError(error));
  }

  const pick = <T,>(defs: DataTableColumn<T>[], ids: string[]) =>
    ids.map((id) => defs.find((def) => def.id === id)).filter((def): def is DataTableColumn<T> => !!def);

  function periodicTable(report: PeriodicTrialBalanceResponse) {
    const defs: DataTableColumn<PeriodicBalanceRow>[] = [
      { id: 'code', header: 'Code', headerClassName: 'w-100', cell: (row) => row.accountCode },
      {
        id: 'name',
        header: 'Account',
        cell: (row) => row.accountName,
        footer: 'Profit',
        footerClassName: 'text-align-right',
      },
      { id: 'type', header: 'Type', headerClassName: 'w-200', cell: (row) => row.accountTypeName },
      { id: 'dimension', header: 'Dimension', headerClassName: 'w-150', cell: (row) => row.dimensionValue },
      {
        id: 'debit',
        header: 'Debit',
        headerClassName: NUMERIC,
        cellClassName: 'text-align-right',
        cell: (row) => money(row.debit),
        footer: money(report.totalDebit),
        footerClassName: 'text-align-right',
      },
      {
        id: 'credit',
        header: 'Credit',
        headerClassName: NUMERIC,
        cellClassName: 'text-align-right',
        cell: (row) => money(row.credit),
        footer: money(report.totalCredit),
        footerClassName: 'text-align-right',
      },
      {
        id: 'amount',
        header: 'Amount',
        headerClassName: NUMERIC,
        cellClassName: 'text-align-right',
        cell: (row) => money(row.amount),
        footer: money(report.totals.profit),
        footerClassName: 'text-align-right',
      },
    ];
    return (
      <div className="mat-table-wrapper">
        <DataTable
          stickyHeader
          columns={pick(defs, s.periodicColumns)}
          rows={report.rows}
          rowKey={(_, index) => index}
          showFooter
        />
      </div>
    );
  }

  function varianceTable(report: VarianceReportResponse) {
    const numeric = (
      id: string,
      header: string,
      cell: (row: VarianceRow) => string,
      footer: string,
    ): DataTableColumn<VarianceRow> => ({
      id,
      header,
      headerClassName: NUMERIC,
      cellClassName: 'text-align-right',
      cell,
      footer,
      footerClassName: 'text-align-right',
    });
    const defs: DataTableColumn<VarianceRow>[] = [
      { id: 'code', header: 'Code', headerClassName: 'w-100', cell: (row) => row.accountCode },
      {
        id: 'name',
        header: 'Account',
        cell: (row) => row.accountName,
        footer: 'Profit',
        footerClassName: 'text-align-right',
      },
      { id: 'dimension', header: 'Dimension', headerClassName: 'w-150', cell: (row) => row.dimensionValue },
      numeric('current', report.periodName, (row) => money(row.currentAmount), money(report.currentTotals.profit)),
      numeric(
        'prior',
        report.priorPeriodName || 'Prior',
        (row) => money(row.priorAmount),
        money(report.priorTotals.profit),
      ),
      numeric('budget', 'Budget', (row) => money(row.budgetAmount), money(report.budgetTotals.profit)),
      numeric('budgetvariance', 'Variance', (row) => money(row.budgetVariance), money(report.profitBudgetVariance)),
      numeric(
        'budgetpercent',
        'Variance %',
        (row) => percent(row.budgetVariancePercent),
        percent(report.profitBudgetVariancePercent),
      ),
    ];
    return (
      <div className="mat-table-wrapper">
        <DataTable
          stickyHeader
          columns={pick(defs, s.varianceColumns)}
          rows={report.rows}
          rowKey={(_, index) => index}
          showFooter
        />
      </div>
    );
  }

  function multiYearTable(report: MultiYearReportResponse) {
    const columns: DataTableColumn<MultiYearReportResponse['rows'][number]>[] = [
      { id: 'code', header: 'Code', headerClassName: 'w-100', cell: (row) => row.accountCode },
      {
        id: 'name',
        header: 'Account',
        cell: (row) => row.accountName,
        footer: 'Profit',
        footerClassName: 'text-align-right',
      },
      ...report.columns.map((column, index): DataTableColumn<MultiYearReportResponse['rows'][number]> => ({
        id: `year${index}`,
        header: column.name,
        headerClassName: NUMERIC,
        cellClassName: 'text-align-right',
        cell: (row) => money(row.amounts[index]),
        footer: money(report.totals[index]?.profit),
        footerClassName: 'text-align-right',
      })),
    ];
    return (
      <div className="mat-table-wrapper">
        <DataTable stickyHeader columns={columns} rows={report.rows} rowKey={(_, index) => index} showFooter />
      </div>
    );
  }

  return (
    <SideMenuPage menu="reports" contentClassName="tr-periodic-trialbalance">
      {/* Design review 2 Nov 2026: Generate is the page's action, so it sits top-right in the sticky page header. */}
      <PageHeader title="Periodic Trial Balance">
        <MatButton className="action-button primary-button" icon="sync" onClick={generate}>
          <span>Generate</span>
        </MatButton>
      </PageHeader>
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-right mb-1">
            <Select
              className="w-270"
              items={s.periods}
              bindLabel="name"
              bindValue="id"
              placeholder="Select Management Period"
              value={s.selectedPeriodId}
              onChange={(value) => set({ selectedPeriodId: value as string | null })}
            />
            <Select
              className="w-200"
              items={priorPeriods}
              bindLabel="name"
              bindValue="id"
              placeholder="Prior period"
              value={s.selectedPriorPeriodId}
              onChange={(value) => set({ selectedPriorPeriodId: value as string | null })}
            />
            <Select
              className="w-180"
              items={s.dimensions}
              bindLabel="name"
              bindValue="code"
              placeholder="All dimensions"
              value={s.selectedDimensionCode}
              onChange={(value) => {
                set({ selectedDimensionCode: value as string | null });
                onDimensionChange();
              }}
            />
            <Select
              className="w-180"
              items={s.dimensionValues}
              bindLabel="name"
              bindValue="code"
              placeholder="All members"
              value={s.selectedDimensionValue}
              onChange={(value) => set({ selectedDimensionValue: value as string | null })}
            />
            <InputField
              fieldClassName="w-100"
              inputMode="numeric"
              placeholder="Years"
              charMode="number"
              value={s.years}
              onChange={(event) => set({ years: event.target.value })}
            />
            <ExportMenu onExport={exportReport} />
          </div>
        </div>

        {/* #184: the reports are built from the period's snapshot; say so when there is none, rather than show nothing. */}
        {selectedPeriodHasNoSnapshot && (
          <p className="text-muted font-size-12 mb-1">
            This management period has no snapshot yet, so there are no figures to show. Choose its trial balance and
            take the snapshot on{' '}
            <a className="link" onClick={openManagementPeriods}>
              Management Periods
            </a>
            , then generate again.
          </p>
        )}

        {/* #183: where the figures shown come from - the period's snapshot of the trial balance chosen for it. */}
        {shownPeriod && (
          <p className="text-muted font-size-12 mb-1" data-testid="periodic-source">
            {shownPeriod.name} ({shownPeriod.startDate} - {shownPeriod.endDate}) · Trial balance for snapshot:{' '}
            {shownPeriod.trialBalanceRef || '-'} · Snapshot: {snapshotSummary(shownPeriod)}
          </p>
        )}

        <TabGroup
          selectedIndex={s.selectedTab}
          // Material emits selectedIndexChange only when the index really changes.
          onSelectedIndexChange={(index) => {
            if (index !== live.current.selectedTab) {
              onTabChange(index);
            }
          }}
          tabs={[
            { label: 'Periodic Trial Balance', children: s.periodic && periodicTable(s.periodic) },
            { label: 'Variance', children: s.variance && varianceTable(s.variance) },
            { label: 'Multi-year', children: s.multiYear && multiYearTable(s.multiYear) },
          ]}
        />
      </div>
    </SideMenuPage>
  );
}
